import { useParams, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import TimeSlotModal from "../components/TimeSlotModal";
import axios from "axios";
import {
  format,
  startOfDay,
  getDay,
  addDays,
  startOfWeek,
  isSameDay,
  parse,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import API_BASE_URL from "../config";
axios.defaults.withCredentials = true;
// axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
//   "token"
// )}`;
const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const getNextDateOfWeek = (dayName) => {
  const dayIndex = daysOfWeek.indexOf(dayName);
  if (dayIndex === -1) throw new Error(`Invalid day: ${dayName}`);

  const today = new Date();
  const resultDate = new Date(today);
  const diff = (dayIndex + 7 - today.getDay()) % 7;
  resultDate.setDate(today.getDate() + diff);
  resultDate.setHours(0, 0, 0, 0);
  return resultDate;
};
const getTimeSlots = (
  start,
  end,
  interval = 15,
  dayOfWeek = null,
  bookedSlots = [],
  eventLimits = {},
  use24Hour = false
) => {
  const slots = [];
  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  const now = new Date();
  const today = startOfDay(new Date());
  const targetDate = dayOfWeek ? getNextDateOfWeek(dayOfWeek) : null;
  const isToday = targetDate && isSameDay(targetDate, today);

  let startTime = new Date();
  startTime.setHours(startHour, startMinute, 0, 0);

  const endTime = new Date();
  endTime.setHours(endHour, endMinute, 0, 0);

  // Apply buffer_before
  if (eventLimits.buffer_before) {
    startTime = new Date(startTime.getTime() + eventLimits.buffer_before * 60000);
  }

  let totalDuration = 0;

  while (startTime < endTime) {
    const formattedTime = format(startTime, use24Hour ? "HH:mm" : "hh:mm a");

    // Create full datetime for the slot using target date (or fallback to today)
    const slotDateTime = new Date(targetDate || today);
    slotDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

    // Skip if this slot is in the past and it's today
    if (isToday && slotDateTime < now) {
      startTime = new Date(startTime.getTime() + interval * 60000);
      continue;
    }

    // Enforce minimum_notice (e.g., 30 minutes from now)
    if (
      eventLimits.minimum_notice &&
      isToday &&
      slotDateTime < new Date(now.getTime() + eventLimits.minimum_notice * 60000)
    ) {
      startTime = new Date(startTime.getTime() + interval * 60000);
      continue;
    }

    // Check booking limits
    const matchingBookings = bookedSlots?.filter(
      (slot) => slot.day_of_week === dayOfWeek && slot.time === formattedTime
    );
    const seatCount = matchingBookings.length;
    const maxSeats = eventLimits?.max_booking_per_slot || 1;
    if (seatCount >= maxSeats) {
      startTime = new Date(startTime.getTime() + interval * 60000);
      continue;
    }

    slots.push(formattedTime);

    // Enforce limit_total_duration
    if (eventLimits.limit_total_duration) {
      totalDuration += interval;
      if (totalDuration >= eventLimits.limit_total_duration) break;
    }

    // Enforce only_first_slot
    if (eventLimits.only_first_slot) break;

    startTime = new Date(startTime.getTime() + interval * 60000);
  }

  return slots;
};

// helper to convert "Monday" to 1, "Sunday" to 0
const getDayNumber = (day) => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return days.indexOf(day);
};

export default function BookingPage() {
  const { companySlug, eventSlug } = useParams(); // <-- TWO params
  // const { slug } = useParams();
  const [availability, setAvailability] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [view, setView] = useState("column");
  const [use24Hour, setUse24Hour] = useState(false);
  const [slots, setSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [eventSettings, setEventSettings] = useState({});
  const [eventLimits, setEventLimits] = useState({});
  const [eventId, setEventId] = useState();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const bookingId = queryParams.get("bookingId");
  const slug = `${companySlug}/${eventSlug}`; // ← This is your full slug

  const dayNameToNumber = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };
  useEffect(() => {
    fetchAvailability();
  }, [companySlug, eventSlug]);

  useEffect(() => {
    if (selectedDate) {
      generateSlotsForDate(selectedDate);
    }
  }, [selectedDate, availability]);
  const parseTo24Hour = (timeStr) => {
    // Handles formats like "4:30 PM" or "04:30 pm"
    const parsedDate = parse(timeStr.trim(), "hh:mm a", new Date());
    return format(parsedDate, "HH:mm");
  };
  const ensure24Hour = (t) => {
    if (/AM|PM/i.test(t)) {
      return parseTo24Hour(t);
    }
    return t; // Already 24-hour
  };
  const formatTime = (time) => {
    if (!time || typeof time !== "string") return "";

    time = time.trim();

    if (use24Hour) {
      // Convert "hh:mm AM/PM" to "HH:mm"
      if (/AM|PM/i.test(time)) {
        const [timePart, modifier] = time.split(" ");
        if (!timePart || !modifier) return "";

        const [hourStr, minuteStr] = timePart.split(":");
        let hour = parseInt(hourStr, 10);
        let minute = parseInt(minuteStr, 10);

        if (isNaN(hour) || isNaN(minute)) return "";

        if (modifier.toUpperCase() === "PM" && hour !== 12) hour += 12;
        if (modifier.toUpperCase() === "AM" && hour === 12) hour = 0;

        return `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
      }

      // Already in 24-hour format
      return time;
    } else {
      // If it's already a 12-hour format with AM/PM, return as is
      if (/AM|PM/i.test(time)) return time;

      // Convert "HH:mm" to "hh:mm AM/PM"
      const [hourStr, minuteStr] = time.split(":");
      let hour = parseInt(hourStr, 10);
      let minute = parseInt(minuteStr, 10);

      if (isNaN(hour) || isNaN(minute)) return "";

      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;

      return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    }
  };
  const fetchAvailability = async () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const res = await axios.get(
      `${API_BASE_URL}/event/${slug}/slots?timezone=${timezone}`
    );
    setAvailability(res.data.availability);
    setEventId(res.data.availability[0].id);
    setBookedSlots(res.data.booked_slots); // <-- Add this line
    setEventSettings(res.data.event_settings || {});
    setEventLimits(res.data.event_limits || {});
    const dates = res.data.availability.map((a) => a.date);
    console.log(dates);
    setAvailableDates(dates);
  };
  const renderEventPriceNotice = () => {
    if (!eventSettings) return null;

    const isPaid = eventSettings.is_paid; // Adjust based on your backend response
    const price = eventSettings.price; // Adjust based on your backend response

    return (
      <div className="alert alert-success text-center">
        {isPaid
          ? `This is a paid event. Booking Price: $${price}`
          : "This is a free event. No payment required."}
      </div>
    );
  };
  const generateSlotsForDate = (date) => {
    const dayName = daysOfWeek[getDay(date)];
    const match = availability.find((a) => a.day_of_week === dayName);
    if (!match) return setSlots([]);

    const from = new Date(`${format(date, "yyyy-MM-dd")}T${match.start_time}`);
    const to = new Date(`${format(date, "yyyy-MM-dd")}T${match.end_time}`);
    const generated = [];
    const now = new Date();

    let totalDuration = 0; // ⬅️ For limit_total_duration

    while (from < to) {
      const slotDateTime = new Date(from);
      const formattedTime = format(
        slotDateTime,
        use24Hour ? "HH:mm" : "hh:mm a"
      );

      // ⬇️ Enforce minimum_notice
      if (
        eventLimits?.minimum_notice &&
        slotDateTime <
          new Date(now.getTime() + eventLimits.minimum_notice * 60000)
      ) {
        from.setMinutes(
          from.getMinutes() +
            (eventLimits?.slot_interval || match.duration || 15)
        );
        continue;
      }
      const slotDateStr = format(slotDateTime, "MMM dd, yyyy");
      const availableSeats = getAvailableSeats(slotDateStr, formattedTime);
      if (availableSeats > 0) {
        generated.push({
          datetime: slotDateTime.toISOString(),
          time: formattedTime,
          day_of_week: format(slotDateTime, "EEEE"),
          seats_left: availableSeats, // Optional: Pass this if you want to display it
        });

        if (eventLimits?.limit_total_duration) {
          totalDuration += eventLimits?.slot_interval || match.duration || 15;
          if (totalDuration >= eventLimits.limit_total_duration) break;
        }

        if (eventLimits?.only_first_slot) break;
      }

      const dateStr = format(slotDateTime, "MMM dd, yyyy");
      const matching = bookedSlots.filter(
        (bs) => bs.date === dateStr && bs.time === formattedTime
      );
      const maxSeats = eventLimits?.max_booking_per_slot || 1;
      if (matching.length < maxSeats) {
        generated.push({
          datetime: slotDateTime.toISOString(),
          time: formattedTime,
          day_of_week: format(slotDateTime, "EEEE"),
        });

        // ⬇️ Enforce limit_total_duration
        if (eventLimits?.limit_total_duration) {
          totalDuration += eventLimits?.slot_interval || match.duration || 15;
          if (totalDuration >= eventLimits.limit_total_duration) break;
        }

        // ⬇️ Enforce only_first_slot
        if (eventLimits?.only_first_slot) break;
      }

      const interval = eventLimits?.slot_interval || match.duration || 15;
      from.setMinutes(from.getMinutes() + interval);
    }

    setSlots(generated);
  };

  function getAvailableSeats(dateStr, timeStr) {
    const maxSeats = eventLimits?.max_booking_per_slot || 1;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const normalizeTime = (time) => {
      const inputFormat =
        time.includes("AM") || time.includes("PM") ? "hh:mm a" : "HH:mm";

      // Parse the time into a base date
      const parsed = parse(time, inputFormat, new Date());

      // Convert from UTC to browser's local timezone
      const localTime = fromZonedTime(parsed, "UTC"); // Treat the parsed date as UTC and convert

      // Normalize to consistent format for comparison
      return use24Hour
        ? format(localTime, "HH:mm")
        : format(localTime, "hh:mm a");
    };

    const normalizedInputTime = normalizeTime(timeStr);

    const count = bookedSlots.filter(
      (b) => b.date === dateStr && normalizeTime(b.time) === normalizedInputTime
    ).length;

    return Math.max(0, maxSeats - count);
  }

  const renderColumnView = () => (
    <div className="container my-4">
      <h4 className="mb-4 text-center fw700 cttext">
        <span className="etbxx">Available Time Slots</span>
      </h4>
      <div className="row">
        {availability
          .map((slot) => {
            const nextDate = getNextDateOfWeek(slot.day_of_week);
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            const sevenDaysLater = addDays(currentDate, 6);

            if (nextDate < currentDate || nextDate > sevenDaysLater) return null;

            // Generate slots to check if any are available
            const timeSlots = getTimeSlots(
              slot.start_time,
              slot.end_time,
              eventLimits?.slot_interval || slot.duration || 15,
              slot.day_of_week,
              bookedSlots,
              eventLimits,
              use24Hour
            );

            if (timeSlots.length === 0) return null; // Hide day if no slots

            return { slot, nextDate, timeSlots };
          })
          .filter(Boolean)
          .sort((a, b) => a.nextDate - b.nextDate)
          .map(({ slot, nextDate, timeSlots }, i) => {
            const formattedDate = format(nextDate, "MMMM dd, yyyy");
            const dayName = format(nextDate, "EEEE");

            return (
              <div className="col-sm-12 col-md-12 mb-4" key={i}>
                <div className="card h-100 shadow-sm p-3 bxcustm">
                  <h6 className="fw700 mb-3 color11">
                    {dayName}, {formattedDate}
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {timeSlots.map((time, index) => {
                      const [h, m] = ensure24Hour(time).split(":");
                      const slotDate = new Date(nextDate);
                      slotDate.setHours(parseInt(h), parseInt(m), 0, 0);

                      const formattedTime = format(slotDate, use24Hour ? "HH:mm" : "hh:mm a");
                      const availableSeats = getAvailableSeats(formattedDate, formattedTime);
                      const isDisabled = availableSeats === 0;

                      return (
                        <button
                          key={index}
                          className="btn btn-outline-success btn-sm tslbtn2"
                          onClick={() => {
                            setSelectedSlot({
                              ...slot,
                              time,
                              datetime: slotDate.toLocaleString([], {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              }),
                            });
                          }}
                          disabled={isDisabled}
                        >
                          <span className="time-badge">{formatTime(time)}</span>
                          <span className="seats-badge">{availableSeats} seats</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );

  const renderMonthView = () => (
    <div className="container my-4 pt-0">
      <h4 className="mb-4 mt-4 text-center fw700 cttext">
        {renderEventPriceNotice()}
      </h4>
      <div className="row">
        <div className="col-md-3 mb-3">
          <h4 className="mb-4 mt-4 text-center fw700 cttext">
            <span className="etbxx">Select a Date</span>
          </h4>
          <div className="card shadow-sm p-3">
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              tileContent={({ date, view }) => {
                if (eventSettings?.hide_notes_in_calendar) {
                  return null;
                }
              }}
              tileDisabled={({ date }) => {
                const today = startOfDay(new Date());
                const current = startOfDay(date);
                const maxDate = addDays(today, 30); // Limit to 30 days
                return current < today || current > maxDate;
              }}
            />
          </div>
        </div>
        <div className="col-md-9">
          {selectedDate && renderTimeSlotsForDate(selectedDate)}
        </div>
      </div>
    </div>
  );

  const renderTimeSlotsForDate = (date) => {
    if (!date || !availability || availability.length === 0) return null;

    const dayName = daysOfWeek[getDay(date)];
    const availableDay = availability.find(
      (slot) => slot.day_of_week === dayName
    );

    if (!availableDay) {
      return (
        <h5 className="text-muted mt-4 text-center">
          No slots available on {dayName}.
        </h5>
      );
    }

    const interval = eventLimits?.slot_interval || availableDay.duration || 15;
    const slots = getTimeSlots(
      availableDay.start_time,
      availableDay.end_time,
      interval,
      dayName,
      bookedSlots,
      eventLimits,
      use24Hour
    );

    if (!slots || slots.length === 0) {
      return (
        <p className="text-muted mt-3 text-center">
          No available slots on {format(date, "EEEE, MMMM d")}.
        </p>
      );
    }

    return (
      <div className="mt-4 text-center mb-4">
        <h4 className="mb-4 text-center fw700 cttext">
          <span className="etbxx">
            Available Slots for{" "}
            <span className="color3">{format(date, "EEEE, MMMM d")}</span>
          </span>
        </h4>
        <div className="d-flex flex-wrap gap-2" style={{ marginBottom: 100 }}>
          {slots.map((time, index) => {
            // ⏱ Parse formatted time string back into a valid Date
            const parsedTime = parse(
              time,
              use24Hour ? "HH:mm" : "hh:mm a",
              new Date(date)
            );
            if (isNaN(parsedTime)) {
              console.warn("Invalid time:", time);
              return null;
            }
            const formattedDate = format(date, "MMM dd, yyyy");
            const availableSeats = getAvailableSeats(formattedDate, time);
            const isDisabled = availableSeats === 0;
            return (
              <button
                key={index}
                className="btn btn-outline-success btn-sm tslbtn"
                onClick={() =>
                  setSelectedSlot({
                    time,
                    datetime: parsedTime.toLocaleString([], {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true, // This ensures the 12-hour format (AM/PM)
                    }),
                    day_of_week: dayName,
                  })
                }
                disabled={isDisabled}
              >
                <span className="time-badge">{time}</span>{" "}
                <span className="seats-badge">{availableSeats} seats</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

const renderWeekView = () => {
  const start = startOfWeek(selectedDate, { weekStartsOn: 1 });

  // Filter the weekDays to only include current date + future days
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    addDays(start, i)
  ).filter((date) => {
    // Check if the date is after the selected date or the selected date itself
    return date >= selectedDate;
  });

  return (
    <div className="container my-4">
      <h4 className="mb-4 text-center fw700 cttext">
        <span className="etbxx">Select a Slot</span>
      </h4>
      <div className="row">
        <div className="col-md-3 mb-3">
          <div className="card shadow-sm p-3">
            <Calendar
              value={selectedDate}
              onChange={setSelectedDate}
              tileDisabled={({ date }) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return date < today;
              }}
              tileContent={({ date, view }) => {
                if (eventSettings.hide_notes_in_calendar) return null;
                return <></>;
              }}
            />
          </div>
        </div>

        <div className="col-md-9">
          <div className="row">
            {weekDays.map((date, idx) => {
              const dayIndex = date.getDay();
              const isSelectedDay = isSameDay(date, selectedDate);
              const match = availability.find(
                (a) => dayNameToNumber[a.day_of_week] === dayIndex
              );

              if (!match) {
                return (
                  <div key={idx} className="col-sm-12 col-md-12 mb-3">
                    <div
                      className={`card h-100 shadow-sm p-3 bxcustm ${
                        isSelectedDay ? "border border-success shadow" : ""
                      }`}
                    >
                      <h6 className="mb-2 fw700 mb-3 color11">
                        {format(date, "EEEE, MMM d")}
                      </h6>
                      <p className="text-muted">No availability</p>
                    </div>
                  </div>
                );
              }

              // Generate slots to check availability
              const timeSlots = getTimeSlots(
                match.start_time,
                match.end_time,
                eventLimits?.slot_interval || match.duration || 15,
                match.day_of_week,
                bookedSlots || [],
                eventLimits || {},
                use24Hour
              );

              if (timeSlots.length === 0) {
                return (
                  <div key={idx} className="col-sm-12 col-md-12 mb-3">
                    <div
                      className={`card h-100 shadow-sm p-3 bxcustm ${
                        isSelectedDay ? "border border-success shadow" : ""
                      }`}
                    >
                      <h6 className="mb-2 fw700 mb-3 color11">
                        {format(date, "EEEE, MMM d")}
                      </h6>
                      <p className="text-muted">No available slots</p>
                    </div>
                  </div>
                );
              }

              const formattedDate = format(date, "MMM dd, yyyy");

              return (
                <div key={idx} className="col-sm-12 col-md-12 mb-3">
                  <div
                    className={`card h-100 shadow-sm p-3 bxcustm ${
                      isSelectedDay ? "border border-success shadow" : ""
                    }`}
                  >
                    <h6 className="mb-2 fw700 mb-3 color11">
                      {format(date, "EEEE, MMM d")}
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                      {timeSlots.map((time, i) => {
                        const formattedTime = formatTime(time);
                        const availableSeats = getAvailableSeats(
                          formattedDate,
                          formattedTime
                        );
                        const isDisabled = availableSeats === 0;

                        // Construct the full datetime by combining the date and time parts
                        const fullDateTimeString = `${format(
                          date,
                          "yyyy-MM-dd"
                        )}T${time}`;
                        const selectedDateTime = use24Hour
                          ? new Date(fullDateTimeString)
                          : parse(
                              fullDateTimeString,
                              "yyyy-MM-dd'T'hh:mm a",
                              new Date()
                            );

                        // Format datetime to a string to avoid React error
                        const formattedDateTime = selectedDateTime.toLocaleString();

                        return (
                          <button
                            key={i}
                            className="btn btn-outline-success btn-sm tslbtn2"
                            onClick={() => {
                              setSelectedSlot({
                                time,
                                datetime: formattedDateTime,
                                day_of_week: match.day_of_week,
                              });
                            }}
                            disabled={isDisabled}
                          >
                            <span className="time-badge">{formattedTime}</span>
                            <span className="seats-badge">{availableSeats} seats</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

  return (
    <div>
      <div className="view-toggle d-flex justify-content-center my-4">
        <button
          onClick={() => setView("month")}
          className={`btn mx-1 ${
            view === "month" ? "btn-danger activee" : "btn-danger"
          }`}
        >
          <i className="bi bi-calendar3 me-2"></i> Month View
        </button>

        <button
          onClick={() => setView("week")}
          className={`btn mx-1 ${
            view === "week" ? "btn-success activee" : "btn-success"
          }`}
        >
          <i className="bi bi-calendar-week me-2"></i> Week View
        </button>

        <button
          onClick={() => setView("column")}
          className={`btn mx-1 ${
            view === "column" ? "btn-primary activee" : "btn-primary"
          }`}
        >
          <i className="bi bi-view-stacked me-2"></i> Column View
        </button>

        <button
          className={`btn mx-1 ${use24Hour ? "btn-info" : "btn-warning"}`}
          onClick={() => setUse24Hour((prev) => !prev)}
        >
          <i className="bi bi-clock me-2"></i>
          {use24Hour ? "12-hour" : "24-hour"} Format
        </button>
      </div>
      {/* {renderEventPriceNotice()} */}
      {view === "month" && renderMonthView()}
      {view === "week" && renderWeekView()}
      {view === "column" && renderColumnView()}

      {selectedSlot && (
        <TimeSlotModal
          show={!!selectedSlot}
          onHide={() => setSelectedSlot(null)}
          slot={selectedSlot}
          slug={slug}
          bookingId={bookingId}
          eventSettings={eventSettings}
          eventLimits={eventLimits}
          eventId={eventId}
          use24Hour={use24Hour}
        />
      )}
    </div>
  );
}
